import Link from 'next/link';
import { Fragment } from 'react';
import { cn } from '@/utils/cn';
import { ArrowRightIcon } from './icons/ArrowRightIcon';

interface BreadcrumbsProps {
  breadcrumbs: Breadcrumb[];
  className?: string;
}

type Breadcrumb = BasicBreadcrumb | LinkedBreadcrumb;

type BasicBreadcrumb = {
  title: string;
};

type LinkedBreadcrumb = BasicBreadcrumb & {
  href: string;
};

export function Breadcrumbs({ breadcrumbs, className }: BreadcrumbsProps) {
  const fullBreadcrumbs: Breadcrumb[] = [
    {
      title: 'Home',
      href: '/',
    },
    ...breadcrumbs,
  ];
  const hasArrows = breadcrumbs.length > 0;
  return (
    <div className={cn('relative w-fit max-w-full', className)}>
      <nav
        aria-label="Breadcrumb navigation"
        className={cn(
          'flex flex-row items-center gap-2 md:gap-4',
          'h-10 rounded-lg bg-medium-gray py-1 pr-4 pl-2 md:pr-4 md:pl-4',
          'max-w-full overflow-x-auto',
          'scrollbar-hide scroll-smooth'
        )}
      >
        {fullBreadcrumbs.map((breadcrumb, index) => {
          const isLast = index === fullBreadcrumbs.length - 1;
          const itemHasArrow = hasArrows && !isLast;
          return (
            <Fragment key={breadcrumb.title}>
              <BreadcrumbItem breadcrumb={breadcrumb} isLast={isLast} />
              {itemHasArrow && (
                <ArrowRightIcon
                  className="size-4 shrink-0"
                  desc="Right Arrow icon"
                  title="Right Arrow"
                />
              )}
            </Fragment>
          );
        })}
      </nav>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-0 right-0 h-full w-4 rounded-r-lg bg-gradient-to-l from-medium-gray to-transparent"
      />
    </div>
  );
}

function BreadcrumbItem({
  breadcrumb,
  isLast,
}: {
  breadcrumb: Breadcrumb;
  isLast: boolean;
}) {
  if (isItemWithHref(breadcrumb)) {
    return (
      <Link
        className="shrink-0 whitespace-nowrap font-normal text-base"
        href={breadcrumb.href}
      >
        {breadcrumb.title}
      </Link>
    );
  }
  return (
    <div className="shrink-0">
      <span
        className={cn(
          'whitespace-nowrap font-medium text-base',
          isLast && 'text-blue-gray'
        )}
      >
        {breadcrumb.title}
      </span>
    </div>
  );
}

function isItemWithHref(
  breadcrumb: Breadcrumb
): breadcrumb is LinkedBreadcrumb {
  return 'href' in breadcrumb;
}
